<template>
  <div class="page-layout">
    <!-- Header Section Begins -->
    <div class="level page-info-section">
      <div class="level-left page-layout-head">Google</div>
      <div class="level-right">
        <date-select-filter
          :filter_items="filters.dateFilter.props"
          :styleProp="filters.dateFilter.styleProp"
          @onDateChange="handleDateChange"
          data-track="Google Analytics change date"
          class="custom-dropdown padding-top-0 padding-right-0"
        />
      </div>
    </div>
    <hr class="horizontal-row"/>
    <!-- Header Section Ends -->

    <div class="pad-left-11">
      <template v-if="isFreeAccountActive">
        <div class="upgrade-box">
          <div class="upgrade-box__header">
            Upgrade
            <img align="right" src="/static/img/competitors/lock.svg"/>
          </div>
          <div class="upgrade-box__main">
            Our Google dashboard is the place to understand how your customers find your website, what they’re searching
            for when they come across your business and some information about your audience.
            <br/>
            <a class="blue-link" href="/billing/edit-plan/g1/monthly">Upgrade</a> your Plan to
            gain access to your Google dashboard
          </div>
        </div>
        <img class="margin-top-15" src="/static/img/social/google/google_free.png"/>
      </template>
      <template v-else>
        <div class="chart-box">
          <div class="chart-box__title">Google analytics</div>
          <div class="chart-box__title main-chart__legend">
            <strong>Total</strong>
            <img src="/static/img/analytics/dark_blue_ellipsis.svg"/>
            Clicks
            <img src="/static/img/analytics/light_blue_ellipsis.svg"/>
            Impressions
          </div>

          <div class="has-spinner" data-track="Google Analytics">
            <img
              class="has-spinner__spinner"
              src="/static/img/loaders/spinner.svg"
              v-if="mainChart.isLoading"
            />
            <vue-chart
              :columns="mainChart.columns"
              :key="mainChart.keyIdx"
              :options="mainChart.chartOptions"
              :rows="mainChart.chartRows"
              chart-type="LineChart"
            />
          </div>
        </div>
        <div class="is-flex">
          <div data-track="Google Analytics">
            <div class="is-flex margin-bottom-15">
              <div class="simple-box">
                <div class="level margin-bottom-0">
                  <div class="level-left">
                    Total &nbsp;
                    <strong>clicks</strong>
                  </div>
                  <div class="level-right">
                    <tooltip-new content-width="350px" :v2="true">
                      The number of times users have clicked on your Hellopeter business listing and Hellopeter reviews
                      from
                      Google organic searches.
                    </tooltip-new>
                  </div>
                </div>
                <hr class="simple-box__head-hr"/>
                <div class="has-text-centered simple-box__text">{{ mainClicks }}</div>
              </div>
              <div class="simple-box">
                <div class="level margin-bottom-0">
                  <div class="level-left">
                    Average &nbsp;
                    <strong>click through rate</strong>
                  </div>
                  <div class="level-right">
                    <tooltip-new content-width="350px" :v2="true">
                      The percentage of total clicks to total impressions i.e. how many users clicked on your
                      Hellopeter business or review listing from a Google results page.
                    </tooltip-new>
                  </div>
                </div>
                <hr class="simple-box__head-hr"/>
                <div class="has-text-centered simple-box__text">{{ mainCtr }}</div>
              </div>
            </div>

            <div class="is-flex margin-bottom-15">
              <div class="simple-box">
                <div class="level margin-bottom-0">
                  <div class="level-left">
                    Total &nbsp;
                    <strong>impressions</strong>
                  </div>
                  <div class="level-right">
                    <tooltip-new content-width="350px" :v2="true">
                      The number of times your Hellopeter business listing and Hellopeter reviews have been viewed by
                      users searching in Google.
                    </tooltip-new>
                  </div>
                </div>
                <hr class="simple-box__head-hr"/>
                <div class="has-text-centered simple-box__text">{{ mainImpressions }}</div>
              </div>
              <div class="simple-box">
                <div class="level margin-bottom-0">
                  <div class="level-left">
                    Average &nbsp;
                    <strong>position</strong>
                  </div>
                  <div class="level-right">
                    <tooltip-new content-width="350px" :v2="true">
                      The average position in Google search results for your Hellopeter business listing and Hellopeter
                      reviews using the highest position for these results whenever they appeared in search results.
                    </tooltip-new>
                  </div>
                </div>
                <hr class="simple-box__head-hr"/>
                <div class="has-text-centered simple-box__text">{{ mainPosition }}</div>
              </div>
            </div>

            <div class="table-listing simple-box margin-bottom-15">
              <div class="has-text-weight-bold">Search term results</div>
              <table class="table">
                <thead class="has-text-weight-bold">
                <tr>
                  <td>
                    Search term
                    <tooltip-new content-width="350px" :v2="true">
                      The search terms used by users to return your Hellopeter business
                      profile and Hellopeter reviews in Google.
                    </tooltip-new>
                  </td>
                  <td class="clicks-col">Clicks</td>
                  <td class="impressions-col">Impressions</td>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, index) in searchResults.data" :key="index">
                  <td v-text="row['keys'][0]"></td>
                  <td class="clicks-col" v-text="row['clicks']"></td>
                  <td class="impressions-col" v-text="row['impressions']"></td>
                </tr>
                </tbody>
              </table>
              <div class="upgradeRow" v-if="isLitePackActive">
                <img src="/static/img/competitors/lock.svg"/>
                <a
                  class="blue-link"
                  href="/billing/edit-plan/p1/annual"
                >Upgrade</a> to gain access
                to more results
              </div>
              <div class="level pagination-section" v-else>
                <paginator
                  :can-update-url="false"
                  :dataSet="searchResults"
                  :isNewPaginationActive="true"
                  :keyword_string="undefined"
                  @changed="searchResultsPaginatorChange"
                />
              </div>
            </div>

            <div class="table-listing page-list simple-box">
              <div class="has-text-weight-bold">Page results</div>
              <table class="table">
                <thead>
                <tr>
                  <td>
                    Page
                    <tooltip-new content-width="350px" :v2="true">
                      The most viewed and clicked on pages in Google for your Hellopeter
                      business profile and Hellopeter reviews.
                    </tooltip-new>
                  </td>
                  <td class="clicks-col">Clicks</td>
                  <td class="impressions-col">Impressions</td>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, index) in pageResults.data" :key="index">
                  <td v-text="row['keys'][0].substring(26)"></td>
                  <td class="clicks-col" v-text="row['clicks']"></td>
                  <td class="impressions-col" v-text="row['impressions']"></td>
                </tr>
                </tbody>
              </table>
              <div class="upgradeRow" v-if="isLitePackActive">
                <img src="/static/img/competitors/lock.svg"/>
                <a
                  class="blue-link"
                  href="/billing/edit-plan/p1/annual"
                >Upgrade</a> to gain access
                to more results
              </div>
              <div class="level pagination-section" v-else>
                <paginator
                  :can-update-url="false"
                  :dataSet="pageResults"
                  :isNewPaginationActive="true"
                  :keyword_string="undefined"
                  @changed="pageResultsPaginatorChange"
                ></paginator>
              </div>
            </div>
          </div>
          <div class="second-col">
            <template v-if="isLitePackActive">
              <img src="/static/img/social/google/google_lite.png" usemap="#image-map"/>
              <map name="image-map">
                <area
                  alt="Upgrade"
                  coords="170, 30, 208, 18"
                  href="/billing/edit-plan/p1/annual"
                  shape="rect"
                  target
                  title="Upgrade"
                />
                <area
                  alt="Upgrade"
                  coords="253, 387, 291, 375"
                  href="/billing/edit-plan/p1/annual"
                  shape="rect"
                  target
                  title="Upgrade"
                />
                <area
                  alt="Upgrade"
                  coords="267, 747, 226, 757"
                  href="/billing/edit-plan/p1/annual"
                  shape="rect"
                  target
                  title="Upgrade"
                />
              </map>
            </template>
            <template v-else>
              <template v-if="1===2">
                <div class="chart-box mini-chart" data-track="Channel Insights">
                  <div class="chart-box__title">
                    <strong>Channel</strong> Insights
                    <tooltip-new content-width="350px" :v2="true">
                      The number of times users have clicked on your Hellopeter business listing
                      and Hellopeter reviews from all channels.
                    </tooltip-new>
                  </div>

                  <div class="has-spinner">
                    <img
                      class="has-spinner__spinner"
                      src="/static/img/loaders/spinner.svg"
                      v-if="channelChart.isLoading"
                    />
                    <template v-else>
                      <div class="mini-chart__legend">
                        <div
                          class="chart__color-col"
                          v-for="(legend, index) in channelChart.legend"
                          :key="index"
                        >
                          <span :style="{'--boxColor':legend.color}" class="chart__color-block"></span>
                          {{ legend.label }}
                        </div>
                      </div>

                      <vue-chart
                        :columns="channelChart.columns"
                        :key="channelChart.keyIdx"
                        :options="channelChart.chartOptions"
                        :rows="channelChart.chartRows"
                        chart-type="PieChart"
                      />
                    </template>
                  </div>
                </div>

                <div
                  class="chart-box mini-chart age-chart"
                  data-track="Audience Insights - age &amp; gender"
                >
                  <div class="chart-box__title">
                    <strong>Audience</strong> Insights - age &amp; gender
                    <tooltip-new content-width="300px" :v2="true">
                      The age and gender of all the users that view your business profile
                      and reviews on Hellopeter from all channels.
                    </tooltip-new>
                  </div>

                  <div class="is-flex age-chart__legend">
                    <gender-count :male="true" :quantity="maleCount" symbol="%"></gender-count>
                    <gender-count :male="false" :quantity="femaleCount" symbol="%"></gender-count>
                  </div>

                  <div class="has-spinner">
                    <img
                      class="has-spinner__spinner"
                      src="/static/img/loaders/spinner.svg"
                      v-if="ageChart.isLoading"
                    />
                    <vue-chart
                      :columns="ageChart.columns"
                      :key="ageChart.keyIdx"
                      :options="ageChart.chartOptions"
                      :rows="ageChart.chartRows"
                      chart-type="ColumnChart"
                    />
                  </div>
                </div>

                <div class="chart-box mini-chart" data-track="Audience Insights - location">
                  <div class="chart-box__title">
                    <strong>Audience</strong> insights - location
                    <tooltip-new
                      content-width="300px"
                      :v2="true"
                    >The location of all the users that view your business profile and reviews on Hellopeter from all channels.
                    </tooltip-new>
                  </div>

                  <div class="has-spinner">
                    <img
                      class="has-spinner__spinner"
                      src="/static/img/loaders/spinner.svg"
                      v-if="locationChart.isLoading"
                    />
                    <template v-else>
                      <div class="mini-chart__legend">
                        <div
                          class="chart__color-col"
                          v-for="(legend, index) in locationChart.legend"
                          :key="index"
                        >
                          <span :style="{'--boxColor':legend.color}" class="chart__color-block"></span>
                          {{ legend.label }}
                        </div>
                      </div>

                      <vue-chart
                        :columns="locationChart.columns"
                        :key="locationChart.keyIdx"
                        :options="locationChart.chartOptions"
                        :rows="locationChart.chartRows"
                        chart-type="PieChart"
                      />
                    </template>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable indent,no-new,no-undef */
import GenderCount from '../../../components/GenderCount.vue'
import axios from 'axios'
import moment from 'moment'
import qs from 'qs'
import {DateSelectFilter} from '@/components'
import {charts} from '@/mixins'
import {TooltipNew} from '@/widgets'
import Paginator from '../../../components/Paginator.vue'

export default {
  name: 'GoogleAnalytics',
  metaInfo: {
    title: 'Business Portal | Hellopeter - Google Analytics'
  },
  mounted () {
    this.$gtm.trackEvent({
      event: 'pageLoad',
      virtualPageUrl: this.$router.currentRoute.path,
      virtualPageTitle: 'Google Analytics'
    })
  },
  components: {
    GenderCount,
    DateSelectFilter,
    TooltipNew,
    Paginator
  },
  mixins: [charts],
  data () {
    return {
      maleCount: 0,
      femaleCount: 0,
      businessSlug: '',
      mainClicks: 0,
      mainImpressions: 0,
      mainCtr: 0,
      mainPosition: 0,
      mainChart: {
        isLoading: false,
        columns: [],
        keyIdx: 0,
        chartOptions: [],
        chartRows: []
      },
      channelChart: {
        isLoading: false,
        columns: [],
        keyIdx: 0,
        chartOptions: [],
        chartRows: [],
        legend: [],
        legendColor: {
          '(Other)': '#3266cc',
          Direct: '#dc3912',
          'Organic Search': '#fd9901',
          Referral: '#109618',
          Social: '#990199',
          inStock: ['#0099c6', '#dd4477', '#b82e2e', '#cccccc', '#9ad951']
        }
      },
      ageChart: {
        isLoading: false,
        columns: [],
        keyIdx: 0,
        chartOptions: [],
        chartRows: []
      },
      locationChart: {
        isLoading: false,
        columns: [],
        keyIdx: 0,
        chartOptions: [],
        chartRows: [],
        legend: [],
        legendColor: {
          'Eastern Cape': '#3266cc',
          'Free State': '#dc3912',
          Gauteng: '#fd9901',
          'KwaZulu-Natal': '#109618',
          Limpopo: '#990199',
          Mpumalanga: '#0099c6',
          'North West': '#dd4477',
          'Western Cape': '#b82e2e',
          Other: '#cccccc',
          inStock: ['#9ad951']
        }
      },
      searchResults: [],
      pageResults: [],
      filters: {
        dateFilter: {
          label: 'last_6_months',
          endDate: null,
          startDate: null,
          styleProp: {},
          props: [
            {
              name: 'date',
              label: 'Date',
              default: {type: 'days', value: 7, selected: 'Last 7 days'}, // moment.js placeholder in type
              disabled: [],
              elements: [
                'Last 7 days',
                'Previous month',
                'Last 6 months',
                'Last 12 months'
              ]
            }
          ]
        },
        searchResultPage: 1,
        pageResultPage: 1
      }
    }
  },
  created () {
    this.businessSlug = localStorage.getItem('org_slug')

    this.mainChart.chartOptions = JSON.parse(JSON.stringify(this.chartOptions))
    this.mainChart.chartOptions['series'] = {
      0: {
        type: 'line',
        color: '#1d7299'
      },
      1: {
        type: 'line',
        color: '#397EF5'
      }
    }
    this.mainChart.chartOptions['chartArea']['left'] = 50
    this.mainChart.chartOptions['chartArea']['top'] = 30

    this.channelChart.chartOptions = JSON.parse(
      JSON.stringify(this.chartOptions)
    )
    this.channelChart.chartOptions['backgroundColor'] = '#fff'
    this.channelChart.chartOptions['chartArea']['bottom'] = 15
    this.channelChart.chartOptions['height'] = 275

    this.ageChart.chartOptions = JSON.parse(JSON.stringify(this.chartOptions))
    this.ageChart.chartOptions['bar'] = {groupWidth: 42}
    this.ageChart.chartOptions['backgroundColor'] = '#fff'
    this.ageChart.chartOptions['chartArea']['bottom'] = 15
    this.ageChart.chartOptions['chartArea']['right'] = 11
    this.ageChart.chartOptions['chartArea']['left'] = 13
    this.ageChart.chartOptions['height'] = 256
    this.ageChart.chartOptions['isStacked'] = true
    this.ageChart.chartOptions['series'] = {
      0: {
        color: '#397EF5'
      },
      1: {
        color: '#dd4477'
      }
    }
    this.ageChart.chartOptions['vAxis']['textPosition'] = 'none'

    this.locationChart.chartOptions = JSON.parse(
      JSON.stringify(this.chartOptions)
    )
    this.locationChart.chartOptions['backgroundColor'] = '#fff'
    this.locationChart.chartOptions['chartArea']['bottom'] = 13
    this.locationChart.chartOptions['height'] = 275
  },
  methods: {
    /**
     * Handle Date Dropdown Filter
     * @param {object} event Date data to sort on
     * @return {void}
     */
    handleDateChange (event) {
      if (!this.isFreeAccountActive) {
        Object.assign(this.filters.dateFilter, event)
        this.getSearchTerms()
        this.getPageURLsViewed()

        if (!this.isLitePackActive) {
          this.channelInsightsFilterSelected(event)
          this.audienceInsightsFilterSelected(event)
          this.locationInsightsFilterSelected(event)
        }

        const dateQuery = this.dateFilters(event.label)
        this.getClicksVsImpressions(dateQuery)
        this.getMainClicksTotals(dateQuery)
      }
    },
    /**
     * Get click and impressions
     * @param {string} query url arguments
     * @return {void}
     */
    getClicksVsImpressions (query) {
      if (!this.isFreeAccountActive) {
        this.mainChart.isLoading = true
        this.mainChart.chartRows = []

        axios
          .get(`/analytics/${this.businessSlug}/clicks-vs-impressions?${query}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              let unsortedRows = data.rows
              unsortedRows.sort((a, b) => {
                const dateA = new Date(a[0]).getTime()
                const dateB = new Date(b[0]).getTime()
                return dateA > dateB ? 1 : -1
              })
              this.mainChart.columns = data.columns
              this.mainChart.chartRows = unsortedRows
              this.mainChart.isLoading = false
              this.mainChart.keyIdx++
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      }
    },
    /**
     * Get click and totals
     * @param {string} query url arguments
     * @return {void}
     */
    getMainClicksTotals (query) {
      if (!this.isFreeAccountActive) {
        axios
          .get(`/analytics/${this.businessSlug}/clicks-main-totals?${query}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.mainClicks = data.clicks
              this.mainImpressions = data.impressions
              this.mainCtr = data.ctr
              this.mainPosition = data.position
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      }
    },
    /**
     * Get search terms
     * @return {void}
     */
    getSearchTerms () {
      if (!this.isFreeAccountActive) {
        axios
          .get(
            `/analytics/${this.businessSlug}/search-terms?start_date=${this.filters.dateFilter.startDate}&end_date=${this.filters.dateFilter.endDate}&page=${this.filters.searchResultPage}`
          )
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.searchResults = data
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      }
    },
    /**
     * Get page urls viewed
     * @param {string} query url arguments
     * @return {void}
     */
    getPageURLsViewed (query) {
      if (!this.isFreeAccountActive) {
        axios
          .get(
            `/analytics/${this.businessSlug}/page-urls?start_date=${this.filters.dateFilter.startDate}&end_date=${this.filters.dateFilter.endDate}&page=${this.filters.pageResultPage}`
          )
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.pageResults = data
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      }
    },
    /**
     * Get audience insights on filter
     * @param {object} event event data
     * @return {void}
     */
    audienceInsightsFilterSelected (event) {
      const dateQuery = this.dateFilters(event.label)
      this.getAgeGroups(dateQuery)
      this.getGenderCounts(dateQuery)
    },
    /**
     * Get age groups
     * @param {string} query url arguments
     * @return {void}
     */
    getAgeGroups (query) {
      /* if (!this.isFreeAccountActive) {
        this.ageChart.isLoading = true
        this.ageChart.chartRows = []

        axios
          .get(`/analytics/${this.businessSlug}/age-groups?${query}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.ageChart.columns = data.columns
              this.ageChart.chartRows = data.rows
              this.ageChart.isLoading = false
              this.ageChart.keyIdx++
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      } */
    },
    /**
     * Get gender counts
     * @param {string} query url arguments
     * @return {void}
     */
    getGenderCounts (query) {
      /* this.maleCount = 0
      this.femaleCount = 0
      if (!this.isFreeAccountActive) {
        axios
          .get(`/analytics/${this.businessSlug}/gender-counts?${query}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              if (
                typeof data.male !== 'undefined' &&
                typeof data.female !== 'undefined'
              ) {
                const male = data.male
                const female = data.female
                const total = male + female
                this.maleCount = parseInt(((male / total) * 100).toFixed(0))
                this.femaleCount = parseInt(
                  ((female / total) * 100).toFixed(0)
                )
              }
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      } */
    },
    /**
     * Get channel insights
     * @param {string} query query parameter
     * @return {void}
     */
    getChannelInsights (query) {
      /* if (!this.isFreeAccountActive) {
        this.channelChart.isLoading = true
        this.channelChart.chartRows = []
        this.channelChart.legend = []
        this.channelChart.chartOptions['colors'] = []

        axios
          .get(`/analytics/${this.businessSlug}/channel-counts?${query}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.channelChart.columns = data.columns
              this.channelChart.chartRows = data.rows
              this.channelChart.isLoading = false
              this.channelChart.keyIdx++

              let inStockPointer = -1
              this.channelChart.chartRows.forEach(value => {
                const label = value[0]
                let color = ''
                if (label in this.channelChart.legendColor) {
                  color = this.channelChart.legendColor[label]
                } else {
                  color = this.channelChart.legendColor.inStock[++inStockPointer]
                }
                this.channelChart.legend.push({
                  label,
                  color
                })

                this.channelChart.chartOptions['colors'].push(color)
              })
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      } */
    },
    /**
     * Get filtered channel insights
     * @param {object} event event data
     * @return {void}
     */
    channelInsightsFilterSelected (event) {
      const dateQuery = this.dateFilters(event.label)
      this.getChannelInsights(dateQuery)
    },
    /**
     * Get filtered location insights
     * @param {object} event event data
     * @return {void}
     */
    locationInsightsFilterSelected (event) {
      /* const dateQuery = this.dateFilters(event.label)
      if (!this.isFreeAccountActive) {
        this.locationChart.isLoading = true
        this.locationChart.chartRows = []
        this.locationChart.legend = []
        this.locationChart.chartOptions['colors'] = []

        axios
          .get(`/analytics/${this.businessSlug}/location-counts?${dateQuery}`)
          .then(({data}) => {
            if (typeof data.no_access !== 'undefined') {
              this.isFreeAccount = data.no_access === 'free_account'
              this.isLitePackage = data.no_access === 'lite_account'
            } else {
              this.locationChart.columns = data.columns
              this.locationChart.chartRows = data.rows
              this.locationChart.isLoading = false
              this.locationChart.keyIdx++

              let inStockPointer = -1
              this.locationChart.chartRows.forEach(value => {
                const label = value[0]
                let color = ''
                if (label in this.locationChart.legendColor) {
                  color = this.locationChart.legendColor[label]
                } else {
                  color = this.locationChart.legendColor.inStock[++inStockPointer]
                }
                this.locationChart.legend.push({
                  label,
                  color
                })

                this.locationChart.chartOptions['colors'].push(color)
              })
            }
          })
          .catch(error => {
            console.log('error', error)
          })
      } */
    },
    /**
     * search results pagination
     * @param {int} page for next or previous page
     * @return {void}
     */
    searchResultsPaginatorChange (page) {
      this.filters.searchResultPage = page
      this.getSearchTerms()
    },
    /**
     * page results pagination
     * @param {int} page for next or previous page
     * @return {void}
     */
    pageResultsPaginatorChange (page) {
      this.filters.pageResultPage = page
      this.getPageURLsViewed()
    },
    /**
     * Process start and end dates based on selected filter
     *
     * @param {string} filter The selected drop down filter option
     *
     * @returns {string}
     */
    dateFilters (filter) {
      let endDate = moment()
      let startDate = moment()
      switch (filter) {
        case 'last_7_days':
          endDate.subtract(1, 'day')
          startDate.subtract(7, 'days')
          break
        case 'previous_month':
          endDate.subtract(1, 'month')
          endDate.endOf('month')
          startDate.subtract(1, 'month')
          startDate.startOf('month')
          break
        case 'last_6_months':
          endDate.subtract(1, 'month')
          endDate.endOf('month')
          startDate.subtract(6, 'months')
          startDate.startOf('month')
          break
        case 'last_12_months':
          endDate.subtract(1, 'month')
          endDate.endOf('month')
          startDate.subtract(12, 'months')
          startDate.startOf('month')
          break
        default:
          break
      }

      endDate = moment(endDate).format('YYYY-MM-DD')
      startDate = moment(startDate).format('YYYY-MM-DD')

      return qs.stringify({
        start_date: startDate,
        end_date: endDate
      })
    }
  },
  computed: {
    // check current subscription is free or not
    isFreeAccountActive () {
      let subscriptionData = JSON.parse(localStorage.getItem('billingDetails'))
      return subscriptionData && subscriptionData.subscription.name === 'Free'
    },
    // check current subscription is lite or not
    isLitePackActive () {
      let subscriptionData = JSON.parse(localStorage.getItem('billingDetails'))
      const liteCategory = ['Lite', 'Lite (Monthly)', 'G1 Annual', 'G2 Annual', 'G3 Annual', 'G4 Annual', 'G1 Monthly', 'G2 Monthly', 'G3 Monthly', 'G4 Monthly', 'G1', 'G2', 'G3', 'G4']
      return subscriptionData && (liteCategory.indexOf(subscriptionData.subscription.name) !== -1)
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utilities/_colors.scss";

.page-layout {
  margin-left: -16px;
  margin-right: -5px;
}

.page-layout .page-layout-head {
  font-size: 12px;
  margin-left: 10px;
  color: #58595b;
}

.horizontal-row {
  margin-top: 0;
  margin-bottom: 1rem;
  margin-left: -5%;
  margin-right: -5%;
}

.page-info-section {
  position: relative;
  margin-bottom: 10px;
}

.page-info-section .level-right .padding-bottom-0 {
  width: 302px;
  padding: 0;
}

.page-info-section .dropdown-trigger button {
  padding-left: 20px !important;
}

.chart-box__title {
  padding-left: 15px;
  padding-top: 0;
}

.main-chart__legend {
  font-weight: normal;
  margin-top: 21px;
}

.main-chart__legend strong {
  margin-right: 10px;
}

.main-chart__legend img {
  vertical-align: bottom;
  margin-right: 3px;
}

.main-chart__legend img:last-child {
  margin-left: 8px;
}

.simple-box {
  width: 268px;
  margin-right: 15px;
  padding: 20px 15px 6px;
}

.simple-box__head-hr {
  margin-top: 18px;
  margin-bottom: 8px;
}

.simple-box__text {
  font-size: 24px;
  color: #4d4f5c;
  font-weight: bold;
}

.table-listing {
  padding: 10px 17px 14px 14px;
  font-size: 11px;
  width: 550px;
}

.table {
  width: 100%;
  margin-bottom: 17px;
}

.table td {
  border-bottom: 1px solid #f0f0f0 !important;
}

.table td:first-child {
  padding-left: 0 !important;
  padding-top: 6px !important;
  color: #58595b;
}

.table thead .tooltip-new {
  margin-left: 9px;
}

.clicks-col {
  color: #1d7299;
  font-weight: bold;
  text-align: right;
  width: 70px;
}

.impressions-col {
  width: 135px;
  color: color('blue');
  font-weight: bold;
  text-align: right;
  padding-right: 15px !important;
}

tbody .impressions-col {
  padding-right: 25px !important;
}

.table > tbody > tr {
  background-color: #fff !important;
}

.table tbody td:first-child,
.table tbody td {
  font-size: 12px;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

.pagination-section {
  justify-content: flex-end;
  padding-right: 14px;
}

.second-col {
  max-width: 610px;
  width: 100%;
}

.mini-chart {
  padding-bottom: 0;
  background-color: #fff;
  color: #58595b;
}

.mini-chart .chart-box__title {
  font-weight: normal;
}

.mini-chart > > > .tooltip-new {
  margin-left: 7px;
}

.mini-chart .has-spinner {
  min-height: 36px;
}

.mini-chart__legend {
  margin-left: 16px;
  margin-top: 12px;
}

.chart__color-col {
  font-size: 12px;
  display: inline-block;
  margin-right: 10px;
}

.chart__color-col:first-child {
  margin-bottom: 8px;
}

.chart__color-block {
  display: inline-block;
  background-color: var(--boxColor);
  height: 15px;
  width: 15px;
  margin-right: 7px;
  vertical-align: middle;
}

.age-chart {
  padding-top: 11px;
  padding-bottom: 12px;
}

.age-chart > > > .tooltip-new {
  margin-left: 8px;
}

.age-chart__legend {
  margin-left: 15px;
  margin-top: 9px;
}

.age-chart__legend .gender-count:first-child {
  margin-right: 17px;
}

.age-chart > > > .gender-counter {
  font-size: 24px;
  color: #58595b;
  margin-left: 18px;
}

.upgrade-box__header {
  padding: 10px 26px 6px 25px;
}

.upgradeRow {
  padding: 7px 0 17px 1px;
  color: #707070;
}

.upgradeRow img {
  vertical-align: bottom;
  margin-right: 7px;
}

.page-list .upgradeRow {
  padding-top: 4px;
  padding-bottom: 14px;
}

.custom-dropdown {
  width: 263px !important;
}

.custom-dropdown > > > .button {
  padding: 8px 0;
  min-height: auto !important;
}

.custom-dropdown > > > .button.button-dropdown img {
  height: 17px;
  padding: 0;
}

.custom-dropdown > > > .dropdown-menu {
  margin-top: 3px;
  border-top: 1px solid #dbdbdb !important;
}

.custom-dropdown > > > .border-bottom-dbdbdb {
  padding: 13px !important;
  border-bottom: 1px solid rgba(88, 89, 91, 0.12);
}

.custom-dropdown > > > .fa-angle-down {
  display: none;
}

.custom-dropdown > > > button.button-dropdown::after {
  border: solid 1px #58595b;
  border-right: 0;
  border-top: 0;
  content: " ";
  display: block;
  height: 0.6em;
  pointer-events: none;
  position: absolute;
  -webkit-transform: rotate(-45deg);
  transform: rotate(-45deg);
  -webkit-transform-origin: center;
  transform-origin: center;
  width: 0.6em;
  margin-top: -0.375em;
  right: 1.125em;
  top: 50%;
  z-index: 4;
}
</style>



// WEBPACK FOOTER //
// src/pages/analytics/google/GoogleAnalytics.vue